import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Storage key for auth state
const AUTH_STORAGE_KEY = 'supabase.auth.token';
const FLOW_STATE_KEY = 'supabase.auth.flowState';
const CODE_VERIFIER_KEY = 'supabase.auth.codeVerifier';
const PKCE_STATE_KEY = 'supabase.auth.pkceState';

// Create Supabase client with enhanced configuration
const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: localStorage,
    storageKey: AUTH_STORAGE_KEY
  },
  global: {
    headers: {
      'X-Client-Info': 'meet-and-eat@1.0.0',
      'apikey': supabaseAnonKey
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Function to clear auth state
const clearAuthState = () => {
  console.log('Clearing auth state');
  
  // Clear all auth-related items from localStorage
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.includes('supabase.auth') || 
      key.includes('sb-') || 
      key.includes('pkce-') ||
      key.includes('code-verifier')
    )) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => {
    console.log(`Removing auth-related key: ${key}`);
    localStorage.removeItem(key);
  });
};

// Enhanced session refresh with retry mechanism
const forceSessionRefresh = async (maxRetries = 3): Promise<boolean> => {
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      console.log(`Attempting session refresh (attempt ${retryCount + 1}/${maxRetries})`);
      
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error("Error getting session:", sessionError);
        throw sessionError;
      }
      
      if (!session) {
        console.warn("No session found");
        return false;
      }
      
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        if (refreshError.message.includes('session_not_found')) {
          console.error("Session not found during refresh");
          return false;
        }
        throw refreshError;
      }
      
      if (data.session) {
        console.log("Session refreshed successfully");
        return true;
      }
      
      return false;
    } catch (err) {
      console.error(`Session refresh attempt ${retryCount + 1} failed:`, err);
      retryCount++;
      
      if (retryCount < maxRetries) {
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        continue;
      }
      
      return false;
    }
  }
  
  return false;
};

// Helper function to check if there's an active session with refresh attempt
const checkSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      if (error.message.includes('session_not_found')) {
        const refreshSuccessful = await forceSessionRefresh();
        if (refreshSuccessful) {
          return (await supabase.auth.getSession()).data.session;
        }
      }
      throw error;
    }
    
    return session;
  } catch (err) {
    console.error('Error checking session:', err);
    return null;
  }
};

// Helper function to handle auth state change
const setupAuthListener = (callback: (session: any) => void) => {
  return supabase.auth.onAuthStateChange(async (event, session) => {
    console.log("Auth state changed:", event);
    if (event === 'TOKEN_REFRESHED') {
      console.log('Token refreshed successfully');
    } else if (event === 'SIGNED_IN') {
      console.log('User signed in successfully');
    } else if (event === 'SIGNED_OUT') {
      console.log('User signed out');
    } else if (event === 'USER_UPDATED') {
      console.log('User updated');
    } else if (event === 'SESSION_EXPIRED') {
      console.log('Session expired, attempting refresh');
      await forceSessionRefresh();
    }
    callback(session);
  });
};

// Enhanced error handler with session-specific handling
const handleSupabaseError = async (error: any) => {
  console.error('Supabase error:', error);

  if (error.status === 403 && error.message?.includes('session_not_found')) {
    const refreshSuccessful = await forceSessionRefresh();
    if (!refreshSuccessful) {
      return new Error('Session expired. Please sign in again.');
    }
    return null;
  }

  // Network errors
  if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
    return new Error('Connection error. Please check your internet connection and try again.');
  }

  // Rate limiting
  if (error.status === 429) {
    return new Error('Too many requests. Please try again later.');
  }

  // Authentication errors
  if (error.status === 401) {
    return new Error('Authentication error. Please sign in again.');
  }

  // Database errors
  if (error.code === 'PGRST116') {
    return new Error('Resource not found.');
  }

  if (error.code === 'PGRST204') {
    return new Error('Invalid request. Please check your input.');
  }

  if (error.code === 'PGRST200') {
    return new Error('Database relationship error. Please try again.');
  }

  // Storage errors
  if (error.statusCode === 400 && error.message.includes('storage')) {
    return new Error('Storage error. Please try uploading again.');
  }

  // Generic error fallback
  return error;
};

// Improved helper function to check if Supabase is available
const checkSupabaseConnection = async (retries = 3, delay = 2000): Promise<boolean> => {
  let lastError = null;

  for (let i = 0; i < retries; i++) {
    try {
      console.log(`Checking Supabase connection (attempt ${i + 1}/${retries})...`);
      
      // Create a timeout promise that rejects after 5 seconds
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Connection timeout')), 5000);
      });

      // Try a simple query with timeout
      const queryPromise = supabase
        .from('profiles')
        .select('id')
        .limit(1);

      // Race between the query and timeout
      await Promise.race([queryPromise, timeoutPromise]);
      
      console.log("Supabase connection successful");
      return true;
    } catch (err: any) {
      lastError = err;
      console.warn(`Connection attempt ${i + 1} failed:`, err);
      
      // Handle specific network errors
      if (err.message === 'Failed to fetch' || err.name === 'TypeError' || err.message === 'Connection timeout') {
        console.warn(`Network error detected: ${err.message}`);
      }
      
      if (i < retries - 1) {
        const backoffDelay = delay * Math.pow(2, i);
        console.log(`Retrying in ${backoffDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        continue;
      }
    }
  }
  
  console.error('Failed to connect to Supabase after multiple attempts:', lastError);
  return false;
};

// Function to prepare auth flow
const prepareAuthFlow = async () => {
  // Clear any existing auth state
  clearAuthState();
  
  // Create a new flow state
  const flowState = {
    created: Date.now(),
    id: crypto.randomUUID()
  };
  
  // Store the flow state
  localStorage.setItem(FLOW_STATE_KEY, JSON.stringify(flowState));
  
  console.log('Auth flow prepared with new flow state');
  return true;
};

// Function to generate and store a code verifier for PKCE
const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = Array.from(array, byte => 
    ('0' + (byte & 0xFF).toString(16)).slice(-2)
  ).join('');
  
  // Store the code verifier
  localStorage.setItem(CODE_VERIFIER_KEY, verifier);
  
  console.log('Generated and stored new code verifier for PKCE');
  return verifier;
};

// Function to check venue ownership for a user
const checkVenueOwnership = async (userId: string) => {
  try {
    // Check for restaurants owned by the user
    const { data: restaurants, error: restaurantError } = await supabase
      .from('restaurants')
      .select('*')
      .eq('owner_id', userId)
      .limit(1);

    if (restaurantError) throw restaurantError;

    if (restaurants && restaurants.length > 0) {
      return {
        hasVenue: true,
        venue: restaurants[0],
        venueType: 'restaurant' as const
      };
    }

    // Check for bars owned by the user
    const { data: bars, error: barError } = await supabase
      .from('bars')
      .select('*')
      .eq('owner_id', userId)
      .limit(1);

    if (barError) throw barError;

    if (bars && bars.length > 0) {
      return {
        hasVenue: true,
        venue: bars[0],
        venueType: 'bar' as const
      };
    }

    // No venue found
    return {
      hasVenue: false,
      venue: null,
      venueType: null
    };
  } catch (error) {
    console.error('Error checking venue ownership:', error);
    throw error;
  }
};

export {
  supabase,
  checkSession,
  setupAuthListener,
  handleSupabaseError,
  checkSupabaseConnection,
  forceSessionRefresh,
  clearAuthState,
  prepareAuthFlow,
  generateCodeVerifier,
  checkVenueOwnership
};